import type { Uuid } from '@/types/models/tree'
import { v4 as uuid } from 'uuid'
import { computed, ref } from 'vue'

export type ToastVariant = 'brand' | 'info' | 'success' | 'warning' | 'error'

export type SnackbarPosition = 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right'

export type ToastLink = {
  label: string
  href: string
  external?: boolean
}

export type Toast = {
  id: Uuid
  variant: ToastVariant
  title?: string
  content: string
  link?: ToastLink
  icon?: string
  progress?: boolean
  size?: 'small' | 'large'
  position: SnackbarPosition
  duration: number
}

export type ToastOptions = Partial<Pick<Toast, 'title' | 'link' | 'icon' | 'progress' | 'size' | 'position' | 'duration'>>

const variantDefaults: Partial<Record<ToastVariant, Pick<Toast, 'icon' | 'progress'>>> = {
  success: { icon: 'i-fa-pro-r-circle-check', progress: true },
  error: { icon: 'i-fa-pro-r-circle-xmark', progress: true },
}

export const useNotificationStore = defineStore('notification', () => {
  const notifications = ref<Toast[]>([])

  function addNotification(variant: ToastVariant, content: string, durationOrOptions: number | ToastOptions = 5000) {
    const options = typeof durationOrOptions === 'number' ? { duration: durationOrOptions } : durationOrOptions
    const id = uuid()
    const defaults = variantDefaults[variant]
    const newToast: Toast = {
      id,
      variant,
      content,
      title: options.title,
      link: options.link,
      icon: options.icon ?? defaults?.icon,
      progress: options.progress ?? defaults?.progress,
      size: options.size,
      position: options.position ?? 'top-right',
      duration: options.duration ?? 5000,
    }

    notifications.value.push(newToast)
  }

  function removeNotification(id: string) {
    notifications.value = notifications.value.filter(toast => toast.id !== id)
  }

  const allNotifications = computed(() => notifications.value)

  return {
    notifications: allNotifications,
    addNotification,
    removeNotification,
  }
})
